"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.implementation = exports.documentation = exports.signature = exports.name = void 0;
const is_1 = __importDefault(require("@sindresorhus/is"));
const error_1 = require("../error");
const constants_1 = require("./constants");
exports.name = "lookupTable";
exports.signature = "<a(sa)(sal)o?:j>";
exports.documentation = {
    label: "$lookupTable",
    detail: "$lookupTable(table, filterKeys, filterPredicates[, options])",
    documentation: `Returns row from a lookup table where all filter conditions are satisfied. Supports [wildcard matching](https://www.stedi.com/docs/mappings/how-to/lookup-table-wildcards).
${constants_1.STEDI_FUNCTION_DISCLAIMER}

**Example**

Given following lookup table named "shippingMethods":

| Key | Value     | Preferred |
|-----|-----------|-----------|
| 1   | express   | false     |
| 2   | standard  | false     |
| 2   | overnight | true      |
| 3   | express_* | false     |

| Expression                                                                                       | Result            |
|--------------------------------------------------------------------------------------------------|-------------------|
| $lookupTable($tables.shippingMethods, "Key", "1").Value                                          | "express"         |
| $lookupTable($tables.shippingMethods, "Key", "2").Preferred                                      | ["false", "true"] |
| $lookupTable($tables.shippingMethods, ["Key", "Preferred"], ["2", "true"]).Value                 | "overnight"       |
| $lookupTable($tables.shippingMethods, "Value", "express_aero", { "wildcard": "*" }).Key          | "3"               |
| $lookupTable($tables.shippingMethods, "Value", "express_other_value", { "wildcard": "*" }).Key   | "3"               |`,
    insertText: "\\$lookupTable($1, $2, $3)",
    parameters: [
        {
            label: "table",
            documentation: `Source lookup table. All lookup tables are available inside $tables global variable`,
        },
        {
            label: "filterKeys",
            documentation: `String or array of strings of keys to filter on`,
        },
        {
            label: "filterPredicates",
            documentation: `One or more predicates to filter on. If \`filterKeys\` is an array, \`filterPredicates\` must be an array of the same length.`,
        },
        {
            label: "options",
            optional: true,
            documentation: `Object with optional properties:
  wildcard - wildcard string to use for matching. E.g. if set to "*", then "express_aero" will match "express_*".`,
        },
    ],
};
const implementation = (props) => {
    try {
        const { filterKeys, filterPredicates, options, table } = sanitizeArguments(props);
        return table.filter((row) => {
            return filterKeys.reduce((didPreviousKeysMatch, key, index) => {
                if (!didPreviousKeysMatch) {
                    return false;
                }
                const predicate = filterPredicates[index];
                if (predicate == null) {
                    // Should never get here, we validate that filterPredicates are of the same size as filterKeys at a prior stage,
                    // we handle this case to satisfy Typescript, and be extra type-safe.
                    return false;
                }
                const valueForKey = row[key];
                if (valueForKey == null) {
                    return false;
                }
                if (valueForKey === predicate) {
                    return didPreviousKeysMatch && true;
                }
                if (options.wildcard === "" || options.wildcard === undefined) {
                    return false;
                }
                return (didPreviousKeysMatch &&
                    matchWithWildcard({ value: valueForKey, compareTo: predicate, wildcard: options.wildcard }));
            }, true);
        });
    }
    catch (e) {
        const error = e;
        /**
         * We are adding a `code` to mock a native JsonataError, but using our own `code` value
         * to differentiate from the errors defined by Jsonata itself.
         *
         * See existing codes here: https://github.com/jsonata-js/jsonata/blob/65e854d6bfee1d1413ebff7f1a185834c6c42265/src/jsonata.js#L1933
         */
        const stediErrorWithJsonataCode = error;
        const stediErrorMessagePrefix = error_1.CUSTOM_STEDI_JSONATA_ERROR_MESSAGES[error_1.CustomStediJsonataError.STEDI003];
        const functionArguments = [
            `table of type \`${typeof props.table}\``,
            `filterKeys=\`${props.filterKeys}\``,
            `filterPredicates=\`${props.filterPredicates}\``,
        ];
        if (props.options !== undefined) {
            functionArguments.push(`options=\`${JSON.stringify(props.options)}\``);
        }
        const functionArgumentsString = functionArguments.join(", ");
        stediErrorWithJsonataCode.code = error_1.CustomStediJsonataError.STEDI003;
        stediErrorWithJsonataCode.message = `${stediErrorMessagePrefix}: ${error.message}, with arguments: ${functionArgumentsString}`;
        throw stediErrorWithJsonataCode;
    }
};
exports.implementation = implementation;
const matchWithWildcard = ({ compareTo, value, wildcard }) => {
    if (compareTo == null) {
        return false;
    }
    if (!value.includes(wildcard)) {
        return false;
    }
    const regexStringBody = value.split(wildcard).map(escapeRegexSymbols).join(".*");
    const regexString = `^${regexStringBody}$`;
    const regex = new RegExp(regexString);
    return regex.test(compareTo);
};
const escapeRegexSymbols = (str) => {
    // Adapted from https://stackoverflow.com/a/17886301
    return str.replace(/[-\\/^$*+?.()|[\]{}]/g, "\\$&");
};
const sanitizeArguments = (props) => {
    var _a, _b;
    if (!is_1.default.array(props.table, is_1.default.plainObject)) {
        throw new Error("Expected table to represent a lookup table - array of plain objects with string values");
    }
    if (!is_1.default.string(props.filterKeys) && !is_1.default.array(props.filterKeys, is_1.default.string)) {
        throw new Error("Expected filterKeys to be a string or an array of strings");
    }
    if (!isStringOrNullOrUndefined(props.filterPredicates) &&
        !is_1.default.array(props.filterPredicates, isStringOrNullOrUndefined)) {
        throw new Error("Expected filterPredicates to be a string or an array of strings");
    }
    if (props.options !== undefined && !is_1.default.plainObject(props.options)) {
        throw new Error("Expected options to be an object or undefined");
    }
    if (((_a = props.options) === null || _a === void 0 ? void 0 : _a.wildcard) !== undefined && !is_1.default.string(props.options.wildcard)) {
        throw new Error("Expected options.wildcard to be a string or undefined");
    }
    const filterKeys = is_1.default.array(props.filterKeys) ? props.filterKeys : [props.filterKeys];
    const filterPredicates = is_1.default.array(props.filterPredicates) ? props.filterPredicates : [props.filterPredicates];
    if (filterKeys.length !== filterPredicates.length) {
        throw new Error("2nd and 3rd argument to $lookupTable function need to contain the same number of elements");
    }
    const table = props.table;
    const options = {
        wildcard: (_b = props.options) === null || _b === void 0 ? void 0 : _b.wildcard,
    };
    return { table, filterKeys, filterPredicates, options };
};
const isStringOrNullOrUndefined = (value) => {
    return is_1.default.string(value) || is_1.default.nullOrUndefined(value);
};
